import dayjs from 'dayjs';
import { INITIAL_COST_VERSUS_OUTPUT_MEASURES } from '../../containers/financials/constants';
import { getRecentlyCompletedMonth } from '../../helpers/general-helpers';
import { useFinancialsStore } from './financials-store';
import type {
  AgentCostsCategory,
  AllocationOfCostsMeasures,
  ChartType,
  CostVersusOutputMeasures,
} from './financials-store.type';
import { Charts, ViewBy } from './financials-store.type';

/**
 * Sets the view by in the Financials store.
 *
 * @param {ViewBy} viewBy - The view by to set
 * @return {void}
 */
function setViewBy(viewBy: ViewBy): void {
  useFinancialsStore.setState({ viewBy }, false, '[Financials Store] setViewBy');
}

/**
 * Sets the selected chart in the Financials store.
 *
 * @param {Charts} chart - The chart to set
 * @return {void}
 */
function setChart(chart: Charts): void {
  const state = useFinancialsStore.getState();
  const updates: Record<string, unknown> = { chart };

  // Extend end date to current month when switching to AgentCosts
  if (chart === Charts.AgentCosts && state.chart !== Charts.AgentCosts && state.endDate) {
    const isBeforeCurrentMonth = dayjs(state.endDate).isBefore(dayjs(), 'month');
    if (isBeforeCurrentMonth) {
      updates.endDate = dayjs().endOf('month').toDate();
    }
  }

  // Clamp end date if switching away from AgentCosts and end date is current month
  if (state.chart === Charts.AgentCosts && chart !== Charts.AgentCosts && state.endDate) {
    const isCurrentMonth = dayjs(state.endDate).isSame(dayjs(), 'month');
    if (isCurrentMonth) {
      const nextEndDate = getRecentlyCompletedMonth();
      updates.endDate = nextEndDate;
      if (state.startDate && dayjs(state.startDate).isAfter(nextEndDate, 'day')) {
        updates.startDate = nextEndDate;
      }
    }
  }

  useFinancialsStore.setState(updates, false, '[Financials Store] setChart');
}

/**
 * Sets the selected start date in the Financials store.
 *
 * @param {Date | null} startDate - The selected start date
 * @return {void}
 */
function setStartDate(startDate: Date | null): void {
  useFinancialsStore.setState({ startDate }, false, '[Financials Store] setStartDate');
}

/**
 * Sets the selected end date in the Financials store.
 *
 * @param {Date | null} endDate - The selected end date
 * @return {void}
 */
function setEndDate(endDate: Date | null): void {
  useFinancialsStore.setState({ endDate }, false, '[Financials Store] setEndDate');
}

/**
 * Sets the selected team in the Financials store.
 *
 * @param {string | null} team - The selected team
 * @return {void}
 */
function setTeam(team: string | null): void {
  useFinancialsStore.setState({ team }, false, '[Financials Store] setTeam');
}

/**
 * Sets the selected board in the Financials store.
 *
 * @param {string | null} board - The selected board
 * @return {void}
 */
function setBoard(board: string | null): void {
  useFinancialsStore.setState({ board }, false, '[Financials Store] setBoard');
}

/**
 * Sets the selected work period IDs in the Financials store.
 *
 * @param {string[]} workPeriodIds - The work period IDs to set
 * @return {void}
 */
function setWorkPeriodIds(workPeriodIds: string[]): void {
  useFinancialsStore.setState({ workPeriodIds }, false, '[Financials Store] setWorkPeriodIds');
}

/**
 * Sets the selected allocation of costs measure in the Financials store.
 *
 * @param {AllocationOfCostsMeasures} measure - The measure to set
 * @return {void}
 */
function setAllocationOfCostsMeasure(measure: AllocationOfCostsMeasures): void {
  useFinancialsStore.setState(
    (state) => ({
      filters: {
        ...state.filters,
        allocationOfCosts: { ...state.filters.allocationOfCosts, selectedMeasure: measure },
      },
    }),
    false,
    '[Financials Store] setAllocationOfCostsMeasure',
  );
}

/**
 * Sets the selected allocation of costs trend lines in the Financials store.
 *
 * @param {string[]} trendLines - The trend lines to set
 * @return {void}
 */
function setAllocationOfCostsTrendLines(trendLines: string[]): void {
  useFinancialsStore.setState(
    (state) => ({
      filters: {
        ...state.filters,
        allocationOfCosts: { ...state.filters.allocationOfCosts, selectedTrendLines: trendLines },
      },
    }),
    false,
    '[Financials Store] setAllocationOfCostsTrendLines',
  );
}
/**
 * Sets the selected allocation of costs options in the Financials store.
 *
 * @param {string[]} options - The options to set
 * @return {void}
 */
function setAllocationOfCostsOptions(options: string[]): void {
  useFinancialsStore.setState(
    (state) => ({
      filters: {
        ...state.filters,
        allocationOfCosts: { ...state.filters.allocationOfCosts, selectedOptions: options },
      },
    }),
    false,
    '[Financials Store] setAllocationOfCostsOptions',
  );
}

/**
 * Sets the selected allocation of costs chart type in the Financials store.
 *
 * @param {ChartType} chartType - The chart type to set
 * @return {void}
 */
function setAllocationOfCostsChartType(chartType: ChartType): void {
  useFinancialsStore.setState(
    (state) => ({
      filters: {
        ...state.filters,
        allocationOfCosts: { ...state.filters.allocationOfCosts, selectedChartType: chartType },
      },
    }),
    false,
    '[Financials Store] setAllocationOfCostsChartType',
  );
}

/**
 * Sets the selected chart type in the Financials store.
 *
 * @param {ChartType} chartType - The chart type to set
 * @return {void}
 */
function setCostByInitiativeChartType(chartType: ChartType): void {
  useFinancialsStore.setState(
    (state) => ({
      filters: {
        ...state.filters,
        costByInitiative: { ...state.filters.costByInitiative, selectedChartType: chartType },
      },
    }),
    false,
    '[Financials Store] setCostByInitiativeChartType',
  );
}

/**
 * Sets the selected chart type in the Financials store.
 *
 * @param {ChartType} chartType - The chart type to set
 * @return {void}
 */
function setCostVersusOutputChartType(chartType: ChartType): void {
  useFinancialsStore.setState(
    (state) => ({
      filters: {
        ...state.filters,
        costVersusOutput: {
          ...state.filters.costVersusOutput,
          selectedChartType: chartType,
        },
      },
    }),
    false,
    '[Financials Store] setCostVersusOutputChartType',
  );
}

/**
 * Sets the selected year in the Financials store.
 *
 * @param {number | null} year - The selected year
 * @return {void}
 */
function setTableSelectedYear(year: number | null): void {
  useFinancialsStore.setState(
    (state) => ({ table: { selectedYear: year, searchTerm: state.table.searchTerm } }),
    false,
    '[Financials Store] setTableSelectedYear',
  );
}

/**
 * Sets the search term in the Financials store.
 *
 * @param {string | null} searchTerm - The search term to set
 * @return {void}
 */
function setTableSearchTerm(searchTerm: string | null): void {
  useFinancialsStore.setState(
    (state) => ({ table: { selectedYear: state.table.selectedYear, searchTerm: searchTerm ?? undefined } }),
    false,
    '[Financials Store] setTableSearchTerm',
  );
}

/**
 * Resets the cost versus output filters to their default state.
 *
 * @return {void}
 */
function resetCostVersusOutputFilters(): void {
  useFinancialsStore.setState(
    (state) => ({
      filters: {
        ...state.filters,
        costVersusOutput: {
          ...state.filters.costVersusOutput,
          selectedMeasures: INITIAL_COST_VERSUS_OUTPUT_MEASURES as CostVersusOutputMeasures[],
          selectedTrendLines: [],
        },
      },
    }),
    false,
    '[Financials Store] resetCostVersusOutputFilters',
  );
}

/**
 * Resets the allocation of costs filters to their default state.
 *
 * @return {void}
 */
function resetAllocationOfCostsFilters(): void {
  useFinancialsStore.setState(
    (state) => ({
      filters: {
        ...state.filters,
        allocationOfCosts: {
          ...state.filters.allocationOfCosts,
          selectedOptions: [],
          selectedTrendLines: [],
        },
      },
    }),
    false,
    '[Financials Store] resetAllocationOfCostsFilters',
  );
}

/**
 * Resets the cost by initiative filters to their default state.
 *
 * @return {void}
 */
function resetCostByInitiativeFilters(): void {
  useFinancialsStore.setState(
    (state) => ({
      filters: {
        ...state.filters,
        costByInitiative: { ...state.filters.costByInitiative, selectedInitiativeIds: [], selectedTrendLines: [] },
      },
    }),
    false,
    '[Financials Store] resetCostByInitiativeFilters',
  );
}

/**
 * Toggles a cost versus output measure selection.
 *
 * @param {CostVersusOutputMeasures} measure - The measure to toggle
 * @return {void}
 */
function toggleCostVersusOutputMeasure(measure: CostVersusOutputMeasures): void {
  useFinancialsStore.setState(
    (state) => {
      const currentMeasures = state.filters.costVersusOutput.selectedMeasures;
      const newMeasures = currentMeasures.includes(measure)
        ? currentMeasures.filter((m) => m !== measure)
        : [...currentMeasures, measure];

      return {
        filters: {
          ...state.filters,
          costVersusOutput: {
            ...state.filters.costVersusOutput,
            selectedMeasures: newMeasures,
          },
        },
      };
    },
    false,
    '[Financials Store] toggleCostVersusOutputMeasure',
  );
}

/**
 * Toggles a cost versus output trend line selection.
 *
 * @param {CostVersusOutputMeasures} measure - The measure to toggle
 * @return {void}
 */
function toggleCostVersusOutputTrendLine(measure: CostVersusOutputMeasures): void {
  useFinancialsStore.setState(
    (state) => {
      const currentTrends = state.filters.costVersusOutput.selectedTrendLines;
      const newTrends = currentTrends.includes(measure)
        ? currentTrends.filter((m) => m !== measure)
        : [...currentTrends, measure];

      return {
        filters: {
          ...state.filters,
          costVersusOutput: {
            ...state.filters.costVersusOutput,
            selectedTrendLines: newTrends,
          },
        },
      };
    },
    false,
    '[Financials Store] toggleCostVersusOutputTrendLine',
  );
}

/**
 * Toggles a cost by initiative selection.
 *
 * @param {string} initiativeId - The initiative ID to toggle
 * @return {void}
 */
function toggleCostByInitiativeId(initiativeId: string): void {
  useFinancialsStore.setState(
    (state) => {
      const currentIds = state.filters.costByInitiative.selectedInitiativeIds;
      const newIds = currentIds.includes(initiativeId)
        ? currentIds.filter((id) => id !== initiativeId)
        : [...currentIds, initiativeId];

      return {
        filters: {
          ...state.filters,
          costByInitiative: {
            ...state.filters.costByInitiative,
            selectedInitiativeIds: newIds,
          },
        },
      };
    },
    false,
    '[Financials Store] toggleCostByInitiativeId',
  );
}

/**
 * Toggles a cost by initiative trend line selection.
 *
 * @param {string} initiativeId - The initiative ID to toggle
 * @return {void}
 */
function toggleCostByInitiativeTrendLine(initiativeId: string): void {
  useFinancialsStore.setState(
    (state) => {
      const currentTrends = state.filters.costByInitiative.selectedTrendLines;
      const newTrends = currentTrends.includes(initiativeId)
        ? currentTrends.filter((id) => id !== initiativeId)
        : [...currentTrends, initiativeId];

      return {
        filters: {
          ...state.filters,
          costByInitiative: {
            ...state.filters.costByInitiative,
            selectedTrendLines: newTrends,
          },
        },
      };
    },
    false,
    '[Financials Store] toggleCostByInitiativeTrendLine',
  );
}

/**
 * Toggles an allocation of costs option selection.
 *
 * @param {string} option - The option to toggle
 * @return {void}
 */
function toggleAllocationOfCostsOption(option: string): void {
  useFinancialsStore.setState(
    (state) => {
      const currentOptions = state.filters.allocationOfCosts.selectedOptions;
      const newOptions = currentOptions.includes(option)
        ? currentOptions.filter((o) => o !== option)
        : [...currentOptions, option];

      return {
        filters: {
          ...state.filters,
          allocationOfCosts: {
            ...state.filters.allocationOfCosts,
            selectedOptions: newOptions,
          },
        },
      };
    },
    false,
    '[Financials Store] toggleAllocationOfCostsOption',
  );
}

/**
 * Toggles an allocation of costs trend line selection.
 *
 * @param {string} option - The option to toggle
 * @return {void}
 */
function toggleAllocationOfCostsTrendLine(option: string): void {
  useFinancialsStore.setState(
    (state) => {
      const currentTrends = state.filters.allocationOfCosts.selectedTrendLines;
      const newTrends = currentTrends.includes(option)
        ? currentTrends.filter((o) => o !== option)
        : [...currentTrends, option];

      return {
        filters: {
          ...state.filters,
          allocationOfCosts: {
            ...state.filters.allocationOfCosts,
            selectedTrendLines: newTrends,
          },
        },
      };
    },
    false,
    '[Financials Store] toggleAllocationOfCostsTrendLine',
  );
}

/**
 * Sets the selected chart type for agent costs in the Financials store.
 *
 * @param {ChartType} chartType - The chart type to set
 * @return {void}
 */
function setAgentCostsChartType(chartType: ChartType): void {
  useFinancialsStore.setState(
    (state) => ({
      filters: {
        ...state.filters,
        agentCosts: { ...state.filters.agentCosts, selectedChartType: chartType },
      },
    }),
    false,
    '[Financials Store] setAgentCostsChartType',
  );
}

/**
 * Sets the view by mode for agent costs in the Financials store.
 *
 * @param {'cost' | 'tokens'} viewBy - The view by mode to set
 * @return {void}
 */
function setAgentCostsViewBy(viewBy: 'cost' | 'tokens'): void {
  useFinancialsStore.setState(
    (state) => ({
      filters: {
        ...state.filters,
        agentCosts: { ...state.filters.agentCosts, viewBy },
      },
    }),
    false,
    '[Financials Store] setAgentCostsViewBy',
  );
}

/**
 * Sets the selected category for agent costs in the Financials store.
 * Resets selected options and trend lines when category changes.
 *
 * @param {AgentCostsCategory} category - The category to set
 * @return {void}
 */
function setAgentCostsCategory(category: AgentCostsCategory): void {
  useFinancialsStore.setState(
    (state) => ({
      filters: {
        ...state.filters,
        agentCosts: {
          ...state.filters.agentCosts,
          selectedCategory: category,
          selectedOptions: [],
          selectedTrendLines: [],
        },
      },
    }),
    false,
    '[Financials Store] setAgentCostsCategory',
  );
}

/**
 * Sets the agent costs selected options array.
 *
 * @param {string[]} options - The options to set
 * @return {void}
 */
function setAgentCostsOptions(options: string[]): void {
  useFinancialsStore.setState(
    (state) => ({
      filters: {
        ...state.filters,
        agentCosts: { ...state.filters.agentCosts, selectedOptions: options },
      },
    }),
    false,
    '[Financials Store] setAgentCostsOptions',
  );
}

/**
 * Toggles an agent costs option selection.
 *
 * @param {string} option - The option to toggle
 * @return {void}
 */
function toggleAgentCostsOption(option: string): void {
  useFinancialsStore.setState(
    (state) => {
      const currentOptions = state.filters.agentCosts.selectedOptions;
      const newOptions = currentOptions.includes(option)
        ? currentOptions.filter((o) => o !== option)
        : [...currentOptions, option];
      return {
        filters: {
          ...state.filters,
          agentCosts: { ...state.filters.agentCosts, selectedOptions: newOptions },
        },
      };
    },
    false,
    '[Financials Store] toggleAgentCostsOption',
  );
}

/**
 * Toggles an agent costs trend line selection.
 *
 * @param {string} option - The option to toggle
 * @return {void}
 */
function toggleAgentCostsTrendLine(option: string): void {
  useFinancialsStore.setState(
    (state) => {
      const currentTrends = state.filters.agentCosts.selectedTrendLines;
      const newTrends = currentTrends.includes(option)
        ? currentTrends.filter((o) => o !== option)
        : [...currentTrends, option];
      return {
        filters: {
          ...state.filters,
          agentCosts: { ...state.filters.agentCosts, selectedTrendLines: newTrends },
        },
      };
    },
    false,
    '[Financials Store] toggleAgentCostsTrendLine',
  );
}

/**
 * Sets the selected agent source for agent costs in the Financials store.
 *
 * @param {string} source - The agent source to set
 * @return {void}
 */
function setAgentCostsAgentSource(source: string): void {
  useFinancialsStore.setState(
    (state) => ({
      filters: {
        ...state.filters,
        agentCosts: { ...state.filters.agentCosts, selectedAgentSource: source },
      },
    }),
    false,
    '[Financials Store] setAgentCostsAgentSource',
  );
}

/**
 * Resets the agent costs filters to their default state.
 *
 * @return {void}
 */
function resetAgentCostsFilters(): void {
  useFinancialsStore.setState(
    (state) => ({
      filters: {
        ...state.filters,
        agentCosts: {
          ...state.filters.agentCosts,
          selectedOptions: [],
          selectedTrendLines: [],
        },
      },
    }),
    false,
    '[Financials Store] resetAgentCostsFilters',
  );
}

/**
 * Resets all financials filters to their default state.
 * This is useful when changing portfolios to ensure filters are relevant to the new context.
 *
 * @return {void}
 */
function resetAllFinancialsFilters(): void {
  useFinancialsStore.setState(
    (state) => ({
      filters: {
        ...state.filters,
        costVersusOutput: {
          ...state.filters.costVersusOutput,
          selectedTrendLines: [],
        },
        allocationOfCosts: {
          ...state.filters.allocationOfCosts,
          selectedOptions: [],
          selectedTrendLines: [],
        },
        costByInitiative: {
          ...state.filters.costByInitiative,
          selectedInitiativeIds: [],
          selectedTrendLines: [],
        },
        agentCosts: {
          ...state.filters.agentCosts,
          selectedAgentSource: 'all',
          selectedOptions: [],
          selectedTrendLines: [],
        },
      },
    }),
    false,
    '[Financials Store] resetAllFinancialsFilters',
  );
}

export {
  resetAgentCostsFilters,
  resetAllFinancialsFilters,
  resetAllocationOfCostsFilters,
  resetCostByInitiativeFilters,
  resetCostVersusOutputFilters,
  setAgentCostsAgentSource,
  setAgentCostsCategory,
  setAgentCostsChartType,
  setAgentCostsOptions,
  setAgentCostsViewBy,
  setAllocationOfCostsChartType,
  setAllocationOfCostsMeasure,
  setAllocationOfCostsOptions,
  setAllocationOfCostsTrendLines,
  setBoard,
  setChart,
  setCostByInitiativeChartType,
  setCostVersusOutputChartType,
  setEndDate,
  setStartDate,
  setTableSearchTerm,
  setTableSelectedYear,
  setTeam,
  setViewBy,
  setWorkPeriodIds,
  toggleAgentCostsOption,
  toggleAgentCostsTrendLine,
  toggleAllocationOfCostsOption,
  toggleAllocationOfCostsTrendLine,
  toggleCostByInitiativeId,
  toggleCostByInitiativeTrendLine,
  toggleCostVersusOutputMeasure,
  toggleCostVersusOutputTrendLine,
};
